import React, { useState, useCallback, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useWindowSize } from '../utils/useWindowSize';
import { getSessionPayoutLines, getSessionPayoutSummary } from '../utils/api';
import { FiRefreshCw } from 'react-icons/fi';
import PayoutSummaryStrip from '../components/payouts/PayoutSummaryStrip';
import PayoutLinesTable from '../components/payouts/PayoutLinesTable';
import PayoutActionDialog from '../components/payouts/PayoutActionDialog';
import MarkPaidDialog from '../components/payouts/MarkPaidDialog';
import PayoutSettingsCard from '../components/payouts/PayoutSettingsCard';
import {
  STATUS_LABELS, sumSummary, startOfDayISO, endOfDayISO, apiErrorMessage, fmtRupees,
} from '../utils/payoutFormat';

const inputStyle = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: "'Open Sans', sans-serif", minHeight: 36, background: '#fff' };

// Session Payouts — admin list of per-session healer payout lines, with hold / release / cancel / mark paid.
const SessionPayouts = () => {
  const { isMobile } = useWindowSize();
  const [summary,    setSummary]    = useState([]);
  const [summaryOk,  setSummaryOk]  = useState(true);
  const [lines,      setLines]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pageSize,   setPageSize]   = useState(20);
  const [page,       setPage]       = useState(1);
  const [status,     setStatus]     = useState('');
  const [healerId,   setHealerId]   = useState('');
  const [from,       setFrom]       = useState('');
  const [to,         setTo]         = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [selected,   setSelected]   = useState([]);
  const [dialog,     setDialog]     = useState(null);
  const [notice,     setNotice]     = useState(null);
  const reqRef = useRef(0);

  const loadSummary = useCallback(async () => {
    try {
      const res = await getSessionPayoutSummary();
      setSummary(res.data.data.summary || []);
      setSummaryOk(true);
    } catch (err) {
      setSummary([]);
      setSummaryOk(false);
    }
  }, []);

  const loadLines = useCallback(async () => {
    const reqId = ++reqRef.current;
    if (from && to && from > to) {
      setLines([]); setTotal(0); setLoading(false);
      setError('The "from" date is after the "to" date.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { page };
      if (status)   params.status   = status;
      if (healerId) params.healerId = healerId;
      if (from)     params.from     = startOfDayISO(from);
      if (to)       params.to       = endOfDayISO(to);
      const res = await getSessionPayoutLines(params);
      if (reqId !== reqRef.current) return;
      const d = res.data.data;
      setLines(d.lines || []);
      setTotal(d.total || 0);
      setPageSize(d.pageSize || 20);
    } catch (err) {
      if (reqId !== reqRef.current) return;
      setLines([]); setTotal(0);
      setError(apiErrorMessage(err, 'Could not load session payouts.'));
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [page, status, healerId, from, to]);

  useEffect(() => { loadLines(); }, [loadLines]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setSelected([]); }, [page, status, healerId, from, to]);

  // Mark paid works on ticked "Ready to pay" lines of one healer.
  const selectedLines    = lines.filter(function(l) { return selected.indexOf(l._id) !== -1; });
  const selectedHealerId = selectedLines.length ? selectedLines[0].healerId : '';
  const selectedTotal    = selectedLines.reduce(function(s, l) { return s + (l.amount || 0); }, 0);
  const selectedPayee    = summary.find(function(r) { return String(r.healerId) === String(selectedHealerId); });
  const toggleLine = function(line) {
    setSelected(function(sel) {
      return sel.indexOf(line._id) === -1 ? sel.concat(line._id) : sel.filter(function(id) { return id !== line._id; });
    });
  };
  const finish = function(text, warn) {
    setDialog(null);
    setSelected([]);
    setNotice({ text: text, warn: !!warn });
    loadLines();
    loadSummary();
  };

  const healerNames = {};
  summary.forEach(function(r) { healerNames[r.healerId] = r.healerName; });
  const healerOptions = summary.slice().sort(function(a, b) { return a.healerName.localeCompare(b.healerName); });
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onFilter = function(setter) {
    return function(e) { setter(e.target.value); setPage(1); };
  };
  const refresh = function() { loadLines(); loadSummary(); };

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <div style={{ marginLeft: isMobile ? 0 : 260, padding: isMobile ? '16px' : '32px', minHeight: '100vh', background: '#f4f6f9', fontFamily: "'Open Sans', sans-serif", flex: 1, minWidth: 0 }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1a3d2b', fontFamily: "'Raleway', sans-serif" }}>
            Session Payouts
          </h1>
          <button onClick={refresh} disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#1a7a4a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
            <FiRefreshCw style={{ fontSize: 14 }} /> Refresh
          </button>
        </div>

        <PayoutSettingsCard />

        <PayoutSummaryStrip totals={sumSummary(summary)} unavailable={!summaryOk} isMobile={isMobile} />

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Status
            <select value={status} onChange={onFilter(setStatus)} style={inputStyle}>
              <option value="">All</option>
              {Object.keys(STATUS_LABELS).map(function(k) { return <option key={k} value={k}>{STATUS_LABELS[k]}</option>; })}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Healer
            <select value={healerId} onChange={onFilter(setHealerId)} style={inputStyle}>
              <option value="">All</option>
              {healerOptions.map(function(h) { return <option key={h.healerId} value={h.healerId}>{h.healerName}</option>; })}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Logged from
            <input type="date" value={from} onChange={onFilter(setFrom)} style={inputStyle} />
          </label>
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Logged to
            <input type="date" value={to} onChange={onFilter(setTo)} style={inputStyle} />
          </label>
          {selectedLines.length > 0 && (
            <button onClick={function() { setDialog({ type: 'markPaid' }); }}
              style={{ padding: '9px 16px', background: '#1a7a4a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Mark paid ({selectedLines.length} · {fmtRupees(selectedTotal)})
            </button>
          )}
        </div>

        {notice && (
          <div role="status" style={{ background: notice.warn ? '#fef3c7' : '#d1fae5', color: notice.warn ? '#92400e' : '#065f46', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>{notice.text}</span>
            <button onClick={function() { setNotice(null); }} aria-label="Dismiss" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'inherit' }}>×</button>
          </div>
        )}

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: 8 }}>
          {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 15 }}>Loading...</div>}
          {!loading && !error && lines.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 14 }}>
              No session payout lines match these filters. Lines are created for completed sessions once per-session payouts are turned on.
            </div>
          )}
          {!loading && lines.length > 0 && (
            <PayoutLinesTable lines={lines} healerNames={healerNames} selected={selected} selectedHealerId={selectedHealerId}
              onToggle={toggleLine} onAction={function(action, line) { setDialog({ type: 'action', action: action, line: line }); }} />
          )}
        </div>

        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16, fontSize: 13, color: '#374151' }}>
            <button onClick={function() { setPage(page - 1); }} disabled={page <= 1 || loading} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}>‹ Prev</button>
            <span>Page {page} of {totalPages} · {total} {total === 1 ? 'line' : 'lines'}</span>
            <button onClick={function() { setPage(page + 1); }} disabled={page >= totalPages || loading} style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}>Next ›</button>
          </div>
        )}

        {dialog && dialog.type === 'action' && (
          <PayoutActionDialog action={dialog.action} line={dialog.line}
            healerName={healerNames[dialog.line.healerId] || 'Healer'}
            onClose={function() { setDialog(null); }} onDone={finish} />
        )}
        {dialog && dialog.type === 'markPaid' && selectedLines.length > 0 && (
          <MarkPaidDialog healerId={selectedHealerId} healerName={healerNames[selectedHealerId] || 'Healer'}
            upiId={(selectedPayee || {}).upiId} hasBankDetails={(selectedPayee || {}).hasBankDetails === true}
            lines={selectedLines} onClose={function() { setDialog(null); }} onDone={finish} />
        )}
      </div>
    </div>
  );
};

export default SessionPayouts;
